/**
 * The map viewport as a `/layers/{id}/features?bbox=` window — spec §D5, wave 4.
 *
 * Wave 4 published feeds two orders of magnitude larger than anything the map had drawn
 * before: ~123 000 WPdx water points, ~156 000 GRID3 settlement extents. §J1 budgets a
 * GeoJSON response at 2 MB, so a layer whose §D5 row says `viewport` is fetched with the
 * map's own bounds and re-fetched as the reader pans.
 *
 * **The box is padded and snapped, and both matter.** The pad means a short pan still has
 * features to draw while the next request is in flight. The snap means the *query key* only
 * changes once the map has moved by a fifth of a screen — without it every `moveend`
 * produces a new key, react-query refetches, and a single drag becomes dozens of requests
 * against the §I4 anonymous throttle.
 *
 * **The snap is applied to the box's centre, not to its two edges.** The first version
 * rounded `west` and `east` outward onto a grid independently, which sounds like the same
 * thing and is not: the two edges cross their grid lines at different moments, so a slow
 * drag across one screen produced *thirteen* distinct boxes out of twenty-one `moveend`
 * events — barely a saving. Snapping the centre and then adding a fixed half-span makes the
 * box a function of one quantised number instead of two, and the same drag produces six.
 * (`viewportBbox.test.ts` counts them, which is how the first version was caught.)
 *
 * The half-span carries `stepX / 2` on top of the pad, because a snapped centre can sit
 * half a step away from the real one and the box must still **contain** the viewport — a
 * box that does not is features disappearing at the edge of the screen.
 *
 * A module of its own rather than a helper inside `VectorLayers.tsx`: it is pure arithmetic
 * over four numbers, which is exactly the kind of thing worth testing directly (and a
 * component file that also exports functions defeats fast refresh).
 */
import type L from 'leaflet';

import type { FeatureBbox } from '../../lib/api';

/** How far outside the visible span the request reaches, as a fraction of that span. */
export const BBOX_PAD = 0.15;
/** The grid the box centre is snapped to, as a fraction of the visible span. */
export const BBOX_SNAP = 0.2;

const clampLon = (n: number) => Math.min(180, Math.max(-180, n));
const clampLat = (n: number) => Math.min(90, Math.max(-90, n));

/**
 * Quantise to 1e-6° (~0.1 m) — outward, so the box still contains what it contained.
 *
 * Not cosmetic. `Math.round(x / step) * step` in binary floating point gives 0.6 as
 * `0.6000000000000001`, so two logically identical boxes serialise to two different query
 * keys and refetch each other in a loop. Rounding *outward* rather than to nearest keeps
 * the containment property the pad exists for.
 */
const quantise = (n: number, direction: 'down' | 'up') =>
  (direction === 'down' ? Math.floor(n * 1e6) : Math.ceil(n * 1e6)) / 1e6;

/** `[minx, miny, maxx, maxy]` for `bounds`, padded and snapped outward. */
export function viewportBbox(bounds: L.LatLngBounds): FeatureBbox {
  const west = bounds.getWest();
  const east = bounds.getEast();
  const south = bounds.getSouth();
  const north = bounds.getNorth();
  // A degenerate viewport (a map that has not been sized yet) would otherwise divide by zero
  // and produce NaN edges, which the API answers with a 400.
  // The span is quantised *before* anything is derived from it. `east - west` for the same
  // logical viewport comes back as 1.0000000000000002 at one pan position and
  // 0.9999999999999999 at the next, and every quantity below is a multiple of it — so
  // without this the grid itself jitters and the "same box for a small pan" property
  // dissolves into float noise. Six significant figures is ~0.1 m on a 1° viewport.
  const spanX = Number(Math.max(east - west, 1e-4).toPrecision(6));
  const spanY = Number(Math.max(north - south, 1e-4).toPrecision(6));
  const stepX = spanX * BBOX_SNAP;
  const stepY = spanY * BBOX_SNAP;
  const centreX = Math.round((west + east) / 2 / stepX) * stepX;
  const centreY = Math.round((south + north) / 2 / stepY) * stepY;
  const halfX = spanX * (0.5 + BBOX_PAD) + stepX / 2;
  const halfY = spanY * (0.5 + BBOX_PAD) + stepY / 2;
  return [
    quantise(clampLon(centreX - halfX), 'down'),
    quantise(clampLat(centreY - halfY), 'down'),
    quantise(clampLon(centreX + halfX), 'up'),
    quantise(clampLat(centreY + halfY), 'up'),
  ];
}

/** True when two boxes are the same request — identity, not proximity. */
export const sameBbox = (a: FeatureBbox | null, b: FeatureBbox): boolean =>
  a !== null && a.every((n, i) => n === b[i]);
